import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";
import { usePosSettings } from "../../utils/posSettings";

interface Customer {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
  mobile_number: string | null;
  created_at: string;
  updated_at: string;
}

const CustomersPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // docs/store-channel-access-control-plan.md, Step 9 — blocking message
  // instead of an (always-empty, per the backend filter) table for an
  // Admin whose only store(s) are pos_only, same treatment DeliverySlotsPage
  // already has. Owner is unrestricted; Sales can't reach this page's route
  // at all (roles: ['O','A']).
  const [noEligibleStores, setNoEligibleStores] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    if (!permissions.isAdmin) return;
    authenticatedFetch(`${API_BASE}/store`, { method: "GET" }, cookies.auth)
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((data) => {
        const list = data.data || data || [];
        setNoEligibleStores(list.length > 0 && !list.some((s: any) => s.channel_type !== 'pos_only'));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [debouncedSearchTerm]);

  const settings = usePosSettings();
  const pageSize = settings.itemsPerPage || 25;

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ user_type: 'C', page: String(page), limit: String(pageSize) });
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/user/users?${params}`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data?.users || []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, [cookies.auth, page, pageSize, debouncedSearchTerm]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleDelete = () => {
    if (selectedCustomers.length === 0) { alert("Please select at least one customer to delete"); return; }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const id of selectedCustomers) {
        await authenticatedFetch(`${API_BASE}/user/${id}`, { method: "DELETE" }, cookies.auth);
      }
      setSelectedCustomers([]);
      setShowDeleteConfirm(false);
      await fetchCustomers();
      snack.onResponse({ message: "Customer(s) deleted successfully!", status: 200 });
    } catch (error) {
      snack.onResponse({ message: "Error deleting customer(s)", status: 500 });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
  };


  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Customers</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by name, email, mobile..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.isOwner && (
            <div className={style.actionButtons}>
              <button className={style.actionBtnDanger} onClick={handleDelete}>Delete</button>
            </div>
          )}
        </div>
      </div>

      {noEligibleStores ? (
        <div className={style.loadingState}>This store is POS-only — there are no mobile customers to manage here.</div>
      ) : (
        <>
          <div className={style.statsBar}>
            <span className={style.statChip}>Total Customers: <strong>{totalItems}</strong></span>
          </div>

          <div className={style.tableContainer}>
            <table className={style.dataTable}>
              <thead>
                <tr>
                  {permissions.isOwner && (
                    <th>
                      <input type="checkbox"
                        checked={selectedCustomers.length === customers.length && customers.length > 0}
                        onChange={e => setSelectedCustomers(e.target.checked ? customers.map(c => c.user_id) : [])}
                        className={style.checkbox}
                      />
                    </th>
                  )}
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.user_id} className={style.tableRow}>
                    {permissions.isOwner && (
                      <td>
                        <input type="checkbox"
                          checked={selectedCustomers.includes(c.user_id)}
                          onChange={e => setSelectedCustomers(e.target.checked ? [...selectedCustomers, c.user_id] : selectedCustomers.filter(id => id !== c.user_id))}
                          className={style.checkbox}
                        />
                      </td>
                    )}
                    <td>{c.user_id}</td>
                    <td>{c.user_first_name} {c.user_last_name}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.mobile_number || "—"}</td>
                    <td>{formatDate(c.created_at)}</td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={6} className={style.emptyState}>No customers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />
        </>
      )}

      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Permanently delete {selectedCustomers.length} customer account(s)? This will remove all their orders and data.</p>
            <div className={style.modalButtons}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn} disabled={isLoading}>Cancel</button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>{isLoading ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
